import React, { useContext, useEffect, useState } from "react";
import logo from "assets/img/logos/barlleno-logo-blanco.png";
import { useNavigate } from 'react-router-dom';
import UserContextProvider from "interface/interfaces";
import * as userServices from "service/userServices";
import { useLocalStorage } from "hooks/useLocalStorage";
import menuIcon from "assets/icons/menu.svg";
import ClearIcon from '@mui/icons-material/Clear';

const STORAGE_KEY = "user";

const NavBar = ({ showButtons }: any) => {
  const [sessionLog, setSessionLog] = useState(false);
  const context = useContext(UserContextProvider);
  const navigate = useNavigate();
  const [isActive, setActive] = useState(false);
  const { removeItem } = useLocalStorage();

  // 1) Efecto inicial
  useEffect(() => {
    const userCurrent = userServices.getCurrentUser();
    if (userCurrent != null && userCurrent != undefined) {
      setSessionLog(true);
      context.asignateUser(userCurrent);
    } else {
      setSessionLog(false);
    }
  }, []);

  // 2) Reacciona cuando cambia el contexto
  useEffect(() => {
    const hasCtx = !!context?.user && !!(context.user.id || context.user.uid);
    setSessionLog(hasCtx);
  }, [context?.user]);

  // 3) Reacciona a cambios en localStorage y a evento custom
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const raw = localStorage.getItem(STORAGE_KEY);
        const u = raw ? JSON.parse(raw) : null;
        setSessionLog(!!u && !!(u.id || u.uid));
        try { context.asignateUser?.(u); } catch { }
      }
    };
    const handleAuthChanged = () => {
      const raw = localStorage.getItem(STORAGE_KEY);
      const u = raw ? JSON.parse(raw) : null;
      setSessionLog(!!u && !!(u.id || u.uid));
      try { context.asignateUser?.(u); } catch { }
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("auth:changed", handleAuthChanged as any);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("auth:changed", handleAuthChanged as any);
    };
  }, [context]);

  const logout = () => {
    setSessionLog(false);
    removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    try { context.asignateUser(null); } catch { }
    navigate("/");
  };

  const redirect = (option: string) => {
    option === 'login' ? navigate("/login") : navigate("/register/");
  };

  const goToSection = (sectionId: string) => {
    const onHome = window.location.pathname === '/home';

    if (onHome) {
      const el = document.getElementById(sectionId) || document.querySelector(`[data-anchor="${sectionId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // hash + state para máxima compatibilidad
      navigate(
        { pathname: '/home', hash: `#${sectionId}` },
        { state: { anchor: sectionId }, replace: false }
      );
    }
  };


  return (
    <>
      <div className="absolute z-[21] w-full">
        {/* Desktop navbar */}
        <div className="hidden md:block p-14 ">
          <div className="mx-auto lg:mx-[5rem] px-6">
            {/* 3 columnas: [logo] [links centrados] [acciones derecha] */}
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-6 h-20">
              {/* LOGO (izquierda) */}
              <div className="min-w-[15rem] flex items-center">
                <img
                  src={logo}
                  alt="Logo barllenotickets"
                  className="w-[15rem] cursor-pointer"
                  onClick={() => navigate("/")}
                />
              </div>

              {/* LINKS (centro) — se mantienen centrados y sin saltos */}
              <nav
                className="flex items-center justify-center font-semibold text-white gap-8 select-none"
              >
                {/* Tip: border transparente para evitar salto en hover */}
                <button
                  onClick={() => goToSection('inicio')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Inicio
                </button>

                {sessionLog && (
                  <button
                    onClick={() => navigate('/my-tickets')}
                    className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                               border-b-2 border-transparent hover:border-primary"
                  >
                    Mis Tickets
                  </button>
                )}

                <button
                  onClick={() => goToSection('proxevents')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Próximos Eventos
                </button>

                <button
                  onClick={() => goToSection('review')}
                  className="transition transform hover:-translate-y-1 hover:scale-105 hover:text-primary
                             border-b-2 border-transparent hover:border-primary"
                >
                  Reviews
                </button>

                {/* si agregas más links, clona el patrón; el centro siempre queda centrado */}
              </nav>

              {/* ACCIONES (derecha) — Rojo Vibrante y Congruente */}
              <div className="flex items-center justify-end gap-4 min-w-[320px]">
                {sessionLog ? (
                  <button
                    onClick={logout}
                    className="h-10 px-6 rounded-full border-2 border-primary text-white text-sm font-bold
                               transition-all duration-300 hover:bg-primary hover:scale-105 active:scale-95
                               backdrop-blur-sm bg-primary/10"
                  >
                    Cerrar sesión
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => redirect('login')}
                      className="h-10 px-6 rounded-full border-2 border-primary text-white text-sm font-bold
                                 transition-all duration-300 hover:bg-primary hover:scale-105 active:scale-95
                                 backdrop-blur-sm bg-primary/10"
                    >
                      Iniciar Sesión
                    </button>
                    <button
                      onClick={() => redirect('register')}
                      className="h-10 px-8 rounded-full bg-primary text-white text-sm font-semibold
                                 transition-all duration-300 hover:scale-105 hover:brightness-110 
                                 shadow-[0_0_15px_rgba(232,65,55,0.4)] hover:shadow-[0_0_25px_rgba(232,65,55,0.6)] active:scale-95"
                    >
                      REGISTRARSE
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="w-full justify-between flex md:hidden relative p-7">
          <div>
            <img
              src={logo}
              alt="Logo barllenotickets"
              className="w-36 cursor-pointer"
              onClick={() => navigate("/home")}
            />
          </div>
          <img onClick={() => setActive(!isActive)} src={menuIcon} className="w-12" alt="" />
        </div>

        <div className={`${isActive ? "flex w-full fixed h-screen top-0 z-50" : "hidden"}`}>
          <div
            className="w-[100%] bg-black bg-opacity-70 hover:cursor-pointer"
            onClick={() => setActive(!isActive)}
          />
          <div className="w-[35rem] h-full bg-graffito flex flex-col relative">
            <ClearIcon style={{ fontSize: "32px" }} className="m-3 text-primary" onClick={() => setActive(!isActive)} />
            <div className="h-[60%] w-full flex flex-col font-Montserrat font-bold text-xl items-center justify-center text-center text-white gap-8 my-8 px-5">
              {/* Puedes usar secciones del Home también en mobile */}
              <button onClick={() => { setActive(false); goToSection('inicio'); }}>Home</button>
              <button onClick={() => { setActive(false); goToSection('proxevents'); }}>Próximos Eventos</button>
              <button onClick={() => { setActive(false); goToSection('review'); }}>Reviews</button>

              {sessionLog && <button onClick={() => { setActive(false); navigate("/my-tickets"); }}>Mis Tickets</button>}

              {sessionLog && (
                <button
                  onClick={() => { setActive(false); logout(); }}
                  className="w-full py-3 rounded-full border border-primary/30 text-primary text-lg font-bold
                             bg-primary/5 hover:bg-primary/10 transition-all active:scale-95"
                >
                  Cerrar sesión
                </button>
              )}

              {!sessionLog && showButtons && (
                <div className="flex flex-col w-full gap-4 mt-4">
                  <button
                    className="w-full py-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm text-white font-medium text-lg transition-all active:scale-95"
                    onClick={() => { setActive(false); navigate("/login"); }}
                  >
                    Iniciar sesión
                  </button>
                  <button
                    className="w-full py-3 rounded-full bg-primary text-white font-bold text-lg shadow-lg shadow-primary/20 transition-all active:scale-95"
                    onClick={() => { setActive(false); navigate("/register"); }}
                  >
                    Registrarse
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NavBar;
